import { useCallback, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Maximize2, Pause, PictureInPicture2, Play, RotateCcw, RotateCw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAudio } from '../contexts/AudioContext';
import { usePlaybackSession } from '../contexts/PlaybackSessionContext';
import { PLAYBACK_OWNERS } from '../lib/playbackSession';
import {
  clearMediaSession,
  getYouTubeTimes,
  registerMediaSession,
  seekYouTubeBy,
} from '../lib/browserMediaSession';
import { thumbUrl } from '../lib/imageUrl';

const formatTime = (seconds = 0) => {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
};

const shouldShowPersistentVideoMiniPlayer = ({ session, activeSource, currentTrack }) => Boolean(
  session?.sessionId
  && session.owner === PLAYBACK_OWNERS.MINI
  && activeSource === 'youtube'
  && currentTrack?.type === 'youtube'
);

const PersistentVideoMiniPlayer = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const { activeSource, currentTrack, isLoading, isPlaying, pause, play, setShowPlayer, stopAll, ytPlayerRef } = useAudio();
  const { session, syncSession, transferOwner, pictureInPictureSupported } = usePlaybackSession();

  const visible = shouldShowPersistentVideoMiniPlayer({ session, activeSource, currentTrack });

  const syncTimes = useCallback(() => {
    const times = getYouTubeTimes(ytPlayerRef?.current);
    syncSession(times);
    return times;
  }, [syncSession, ytPlayerRef]);

  const seek = useCallback((seconds) => {
    const position = seekYouTubeBy(ytPlayerRef?.current, seconds);
    syncSession({ position });
  }, [syncSession, ytPlayerRef]);

  useEffect(() => {
    const fullWidthPlayer = document.querySelector('[data-testid="audio-player"]');
    if (!fullWidthPlayer) return undefined;
    const previousDisplay = fullWidthPlayer.style.display;
    if (visible) fullWidthPlayer.style.display = 'none';
    return () => { fullWidthPlayer.style.display = previousDisplay; };
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    syncTimes();
    const timer = window.setInterval(syncTimes, 1000);
    return () => window.clearInterval(timer);
  }, [syncTimes, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    registerMediaSession({ track: currentTrack, isPlaying, onPlay: play, onPause: pause, onSeekBackward: () => seek(-10), onSeekForward: () => seek(10) });
    return () => clearMediaSession();
  }, [currentTrack, isPlaying, pause, play, seek, visible]);

  const expand = () => {
    const prefix = currentTrack.videoType === 'video-mix' ? '/video-mixes' : '/records';
    transferOwner({ owner: PLAYBACK_OWNERS.PLAYBLOCK, reason: 'mini-player-expand' });
    navigate(`${prefix}/${currentTrack.youtubeId}`);
  };

  const close = () => { stopAll(); setShowPlayer(false); };

  const enterPictureInPicture = async () => {
    const video = document.querySelector('video');
    if (!pictureInPictureSupported || !video?.requestPictureInPicture) return;
    try {
      await video.requestPictureInPicture();
      transferOwner({ owner: PLAYBACK_OWNERS.PIP, reason: 'native-pip-enter' });
      video.addEventListener('leavepictureinpicture', () => transferOwner({ owner: PLAYBACK_OWNERS.MINI, reason: 'native-pip-exit' }), { once: true });
    } catch {}
  };

  if (!visible) return null;
  const progress = session.duration > 0 ? Math.min(100, (session.position / session.duration) * 100) : 0;

  return (
    <motion.aside data-testid="persistent-video-mini-player" initial={reduceMotion ? false : { opacity: 0, y: 32, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={reduceMotion ? undefined : { opacity: 0, y: 24, scale: 0.96 }} className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] right-3 z-50 w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-white/10 bg-black/95 shadow-2xl backdrop-blur-xl lg:bottom-24 lg:right-6" aria-label="Persistent video player">
      <div className="flex gap-3 p-3">
        <button type="button" onClick={expand} className="relative h-20 w-28 flex-shrink-0 overflow-hidden rounded-lg bg-white/5" aria-label="Return to full video"><img src={thumbUrl(currentTrack.cover)} alt="" className="h-full w-full object-cover" />{isLoading && <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-xs font-semibold">Loading…</span>}</button>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2"><button type="button" onClick={expand} className="min-w-0 text-left"><p className="truncate text-sm font-semibold text-white">{currentTrack.title}</p><p className="truncate text-xs text-white/60">{currentTrack.artist}</p></button><button type="button" onClick={close} className="rounded-full p-1 text-white/60 hover:bg-white/10 hover:text-white" aria-label="Close video player"><X className="h-4 w-4" /></button></div>
          <div className="mt-3 flex items-center gap-1"><button type="button" onClick={() => seek(-10)} className="rounded-full p-2 hover:bg-white/10" aria-label="Rewind 10 seconds"><RotateCcw className="h-4 w-4" /></button><button type="button" onClick={isPlaying ? pause : play} className="rounded-full bg-primary p-2 text-black" aria-label={isPlaying ? 'Pause video' : 'Play video'}>{isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button><button type="button" onClick={() => seek(10)} className="rounded-full p-2 hover:bg-white/10" aria-label="Forward 10 seconds"><RotateCw className="h-4 w-4" /></button><button type="button" onClick={expand} className="ml-auto rounded-full p-2 hover:bg-white/10" aria-label="Expand video"><Maximize2 className="h-4 w-4" /></button><button type="button" onClick={enterPictureInPicture} disabled={!pictureInPictureSupported} className="rounded-full p-2 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30" aria-label="Enter picture in picture"><PictureInPicture2 className="h-4 w-4" /></button></div>
        </div>
      </div>
      <div className="px-3 pb-3"><div className="h-1 overflow-hidden rounded-full bg-white/15" aria-hidden="true"><div className="h-full bg-primary" style={{ width: `${progress}%` }} /></div><div className="mt-1 flex justify-between text-[10px] text-white/50"><span>{formatTime(session.position)}</span><span>{formatTime(session.duration)}</span></div></div>
    </motion.aside>
  );
};

export { formatTime, shouldShowPersistentVideoMiniPlayer };
export default PersistentVideoMiniPlayer;
